/**
 * Tools for SVG handle on JSON format.
 * @module svgcanvas
 * @license MIT
 *
 * @copyright 2010 Alexis Deveria, 2010 Jeff Schiller
 */
import { getElem, assignAttributes, cleanupElement } from './utilities';
import { NS } from '../common/namespaces';
import SvgCanvas from './svgcanvas';

let currentCanvas_: SvgCanvas = null;
let svgdoc_: Document = null;

/**
* @function module:json.init
* @param {module:json.jsonContext} jsonContext
* @returns {void}
*/
export const init = function (svgCanvas: SvgCanvas) {
    currentCanvas_ = svgCanvas;
    svgdoc_ = currentCanvas_.getDOMDocument();
};

/**
* @typedef {PlainObject} module:svgcanvas.SVGAsJSON
* @property {string} element
* @property {PlainObject<string, string>} attr
* @property {module:svgcanvas.SVGAsJSON[]} children
*/
export interface SVGAsJSON {
    element: string;
    attr: { [key: string]: string };
    children: SVGAsJSON[];
}

/**
* @function module:json.getJsonFromSvgElements Iterate element and return json format
* @param {ArgumentsArray} data - element
* @returns {svgRootElement}
*/
export const getJsonFromSvgElements = (data: SVGElement): SVGAsJSON | string => {
    // Text node
    if (data.nodeType === Node.TEXT_NODE) {
        return data.nodeValue;
    }

    const retval = {
        element: data.tagName,
        // namespace: nsMap[data.namespaceURI],
        attr: {},
        children: []
    };

    
    // Iterate attributes
    let attr: Attr;
    for (let i = 0; ; i++) {
        attr = data.attributes[i];
        if (!attr) {
            break;
        }
        retval.attr[attr.name] = attr.value;
    }

    // Iterate children
    let node: SVGElement;
    for (let i = 0; ; i++) {
        node = data.childNodes[i] as SVGElement;
        if (!node) {
            break;
        }
        retval.children[i] = getJsonFromSvgElements(node);
    }

    return retval;
};

export interface IAddSvgElementJson {
    element: keyof SVGElementTagNameMap;
    attr: {
        [key: string]: string | number;
        id?: string;
    };
    namespace?: string;
    curStyles?: boolean;
    children?: IAddSvgElementJson[];
}

/**
* This should really be an intersection implementing all rather than a union.
* @name module:json.addSVGElementsFromJson
* @type {module:utilities.EditorContext#addSVGElementsFromJson|module:path.EditorContext#addSVGElementFromJson}
*/
export const addSVGElementsFromJson = function <T extends SVGElement = SVGElement>(data: IAddSvgElementJson): T {
    if (typeof data === 'string') {
        return svgdoc_.createTextNode(data) as unknown as T;
    }

    let shape = getElem<T>(data.attr.id);
    // if shape is a path but we need to create a rect/ellipse, then remove the path
    const currentLayer = currentCanvas_.getCurrentDrawing().getCurrentLayer();
    if (shape && data.element !== shape.tagName) {
        shape.remove();
        shape = null;
    }
    if (!shape) {
        const ns = data.namespace || NS.SVG;
        shape = svgdoc_.createElementNS(ns, data.element) as unknown as T;
        if (currentLayer) {
            (currentCanvas_.getCurrentGroup() || currentLayer).append(shape);
        }
    }
    const curShape = currentCanvas_.getCurrentShape();
    if (data.curStyles) {
        assignAttributes(shape, {
            fill: curShape.fill,
            stroke: curShape.stroke,
            'stroke-width': curShape.stroke_width,
            'stroke-dasharray': curShape.stroke_dasharray,
            'stroke-linejoin': curShape.stroke_linejoin,
            'stroke-linecap': curShape.stroke_linecap,
            'stroke-opacity': curShape.stroke_opacity,
            'fill-opacity': curShape.fill_opacity,
            opacity: (curShape.opacity / 2).toString(),
            style: 'pointer-events:inherit'
        }, 100);
    }
    assignAttributes(shape, data.attr, 100);
    cleanupElement(shape);

    // Children
    if (data.children) {
        data.children.forEach((child) => {
            shape.append(addSVGElementsFromJson(child));
        });
    }

    return shape;
};
